import { createContext, useContext, useState, useCallback, useMemo } from 'react';

// Tema chiaro/scuro delle pagine pubbliche.
//
// DEV'ESSERE UNO STATO CONDIVISO, NON UN HOOK CON useState PROPRIO.
// Prima lo era, ed e' stato un difetto vero (2026-09-02, segnalato dal vivo:
// "lo switch non funziona"): dopo lo spostamento dell'ambito al guscio, questo
// hook veniva chiamato in due punti — GuscioTema in App.js e la pagina, che lo
// passa a SiteNav. Due chiamate a useState sono due stati indipendenti: il clic
// sul sole/luna aggiornava quello della PAGINA, il bottone si accendeva e
// localStorage veniva scritto, ma il guscio — che e' l'elemento su cui sta
// data-tema — non ne sapeva niente e la palette non cambiava mai.
//
// Con il contesto la sorgente e' una sola. Il fatto che l'interruttore si
// accendesse e lo sfondo no era esattamente il sintomo di due stati che
// divergono.
//
// Il CHIARO e' il default (ripristinato il 2026-09-07, annullando 8fa1f72
// del 05/09 che aveva messo lo scuro): chi non ha mai toccato l'interruttore
// vede il sito bianco; solo lc-tema='scuro' esplicito tiene lo scuro. Nessun
// prefers-color-scheme, per scelta esplicita.
//
// Lo stato iniziale viene da document.documentElement.dataset.temaIniziale, che
// uno script inline in index.html imposta PRIMA che il bundle venga eseguito:
// senza, chi ha scelto lo scuro vedrebbe un lampo bianco a ogni caricamento,
// perche' React monta dopo il primo disegno della pagina. Per lo stesso motivo
// cambiaTema riscrive quel dataset.
//
// Tutto dentro try/catch: in incognito o con lo storage bloccato l'accesso a
// localStorage puo' lanciare, e un'eccezione qui impedirebbe il montaggio
// dell'intera applicazione.
//
// NOTA: vale per le pagine PUBBLICHE. Vault e pannello admin non lo usano e
// restano scuri (decisione del 2026-09-02): il guscio forza data-tema="scuro"
// su quelle viste indipendentemente da questo stato, che resta memorizzato —
// chi torna sul sito pubblico ritrova la scelta che aveva fatto.

const TemaContext = createContext(null);

function statoIniziale() {
  try {
    if (document.documentElement.dataset.temaIniziale === 'scuro') return 'scuro';
  } catch {}
  // IL CHIARO TORNA A ESSERE IL DEFAULT (2026-09-07), annullando 8fa1f72
  // del 05/09 09:43. Chi non ha mai toccato l'interruttore vede il sito
  // bianco; chi ha scelto lo scuro se lo tiene, perche' la sua scelta e'
  // salvata in lc-tema e viene letta qui sopra e nello script inline di
  // index.html — che restano le due meta' della stessa regola e vanno
  // cambiate insieme, altrimenti il primo frame e il resto della pagina
  // dicono due cose diverse.
  try {
    if (localStorage.getItem('lc-tema') === 'scuro') return 'scuro';
  } catch {}
  return 'chiaro';
}

export function TemaProvider({ children }) {
  const [tema, setTema] = useState(statoIniziale);

  const cambiaTema = useCallback((valore) => {
    // Nessun valore diverso dai due ammessi entra nello stato o nello storage.
    const v = valore === 'scuro' ? 'scuro' : 'chiaro';
    setTema(v);
    try { localStorage.setItem('lc-tema', v); } catch {}
    try {
      if (v === 'scuro') document.documentElement.dataset.temaIniziale = 'scuro';
      else delete document.documentElement.dataset.temaIniziale;
    } catch {}
  }, []);

  const valore = useMemo(() => ({ tema, cambiaTema }), [tema, cambiaTema]);
  return <TemaContext.Provider value={valore}>{children}</TemaContext.Provider>;
}

// Fuori dal provider non si lancia: si restituisce il tema iniziale e un
// cambiaTema che non fa niente. Un componente montato per sbaglio fuori dal
// guscio deve rendersi, non far cadere la pagina.
export function useTema() {
  const ctx = useContext(TemaContext);
  if (ctx) return ctx;
  return { tema: statoIniziale(), cambiaTema: () => {} };
}

export default useTema;
